import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { setBreadcrumbItems } from "../../store/actions";
import axios from "../../utils/axios";
import Swal from 'sweetalert2';

import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Clock, User, BookOpen, BarChart2, Video, Loader, Check } from 'react-feather';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { getProfileImageUrl } from "../../helpers/image_helpers";
import { Upload as TusUpload } from 'tus-js-client';

import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Badge,
    Button,
    FormGroup,
    Label,
    Input,
    Progress
} from 'reactstrap';

const CourseDetails = (props) => {
    const backend_url = process.env.REACT_APP_BACKEND_BASE_URL;
    const { id } = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeVideo, setActiveVideo] = useState(null);
    const [modal, setModal] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [formData, setFormData] = useState({
        videos: [{ title: '', description: '', video: null, uploadId: '', isUploading: false, isUploaded: false }]
    });
    const [videoData, setVideoData] = useState({
        title: '',
        description: '',
        video_id: null
    });

    document.title = "Course Details | Equity Circle - Admin Dashboard";
    const breadcrumbItems = [
        { title: "Courses Management", link: "/courses-management" },
        { title: "Course Details", link: "#" },
    ];

    useEffect(() => {
        props.setBreadcrumbItems('Courses Management', breadcrumbItems);
    }, []);

    const formatDuration = (totalSeconds) => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        if (hours > 0) {
            return `${hours}h ${minutes}m ${seconds}s`;
        } else {
            return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
        }
    };

    useEffect(() => {
        const loadScript = (src) => {
            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = src;
                script.onload = () => resolve();
                script.onerror = () => reject(new Error(`Script load error for ${src}`));
                document.body.appendChild(script);
            });
        };

        const fetchData = async () => {
            try {
                const response = await axios.get(`/api/education-course/${id}`);
                setCourse(response.data);

                // Parse videos if they exist
                if (response.data.videos) {
                    const videos = JSON.parse(response.data.videos);
                    setActiveVideo(videos[0]); // Set first video as active by default
                }

                // Load Cloudflare Stream SDK
                await loadScript('https://embed.cloudflarestream.com/embed/sdk.latest.js');
            } catch (error) {
                console.error('Error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const handleVideoPlay = (video) => {
        setActiveVideo(video);

        // Small delay to ensure iframe is rendered before trying to control it
        setTimeout(() => {
            const iframe = document.getElementById('stream-player');
            if (iframe && window.Stream) {
                const player = window.Stream(iframe);
                player.play().catch(() => {
                    console.log('playback failed, muting to try again');
                    player.muted = true;
                    player.play();
                });
            }
        }, 300);
    };

    const addvideo = () => {
        setModal(true);
    };

    const retryUpload = async (index) => {
        const updatedVideos = [...formData.videos];
        const file = updatedVideos[index].video;
        if (file) {
            await uploadVideoToCloudflare(file, index, true);
        }
    };

    const handleVideoChange = async (index, e) => {
        const { name, files } = e.target;
        const updatedVideos = [...formData.videos];

        if (name === 'video') {
            const file = files[0];
            if (file) {
                // Check file size (e.g., 500MB limit)
                if (file.size > 500 * 1024 * 1024) {
                    toast.warning(`Video ${index + 1} exceeds 500MB limit`);
                    return;
                }

                updatedVideos[index] = {
                    ...updatedVideos[index],
                    video: file,
                    isUploading: true,
                    isUploaded: false
                };

                setFormData(prev => ({
                    ...prev,
                    videos: updatedVideos
                }));

                // Upload to Cloudflare Stream
                await uploadVideoToCloudflare(file, index);
            }
        }
    };

    const uploadVideoToCloudflare = async (file, index, is_retry = false, is_remove = false) => {
        try {
            const uploadId = `video-${index}-${Date.now()}`;
            const filename = `video-${index}-${Date.now()}.mp4`;

            // Update form data with uploadId
            setFormData(prev => {
                const updatedVideos = [...prev.videos];
                updatedVideos[index].uploadId = uploadId;
                updatedVideos[index].isUploading = true;
                return { ...prev, videos: updatedVideos };
            });

            const endpoint = `https://api.cloudflare.com/client/v4/accounts/${process.env.REACT_APP_CLOUDFLARE_ACCOUNT_ID}/stream?tus=true`;

            // Create a new upload
            const upload = new TusUpload(file, {
                endpoint: endpoint,
                headers: {
                    Authorization: `Bearer ${process.env.REACT_APP_CLOUDFLARE_API_TOKEN}`,
                },
                chunkSize: 50 * 1024 * 1024, // 50MB chunks
                retryDelays: [0, 1000, 3000, 5000],
                metadata: {
                    filename: filename,
                    filetype: file.type
                },
                onError: (error) => {
                    console.error('Upload error:', error);
                    setFormData(prev => {
                        const updatedVideos = [...prev.videos];
                        updatedVideos[index].isUploading = false;
                        updatedVideos[index].isUploaded = false;
                        return { ...prev, videos: updatedVideos };
                    });
                    toast.error(`Upload failed for video ${index + 1}: ${error.message}`);
                },
                onProgress: (bytesUploaded, bytesTotal) => {
                    const progress = Math.round((bytesUploaded / bytesTotal) * 100);
                    setUploadProgress(prev => ({
                        ...prev,
                        [uploadId]: progress
                    }));
                },
                onSuccess: () => {
                    // Get the video ID from the upload URL
                    const videoUrl = upload.url;
                    const uid = videoUrl.split('/').pop().split('?')[0];

                    setVideoData({...videoData, video_id: uid});

                    setFormData(prev => {
                        const updatedVideos = [...prev.videos];
                        updatedVideos[index].isUploading = false;
                        updatedVideos[index].isUploaded = true;
                        updatedVideos[index].videoId = uid;
                        updatedVideos[index].name = filename;
                        updatedVideos[index].size = file.size;
                        return { ...prev, videos: updatedVideos };
                    });

                    toast.success(`Video ${index + 1} uploaded successfully!`);
                }
            });

            if (is_remove) {
                upload.abort();
            }

            // Check if there's a previous upload to resume
            upload.findPreviousUploads().then((previousUploads) => {
                if (previousUploads.length && is_retry) {
                    upload.resumeFromPreviousUpload(previousUploads[0]);
                } else {
                    upload.start();
                }
            });

        } catch (error) {
            console.error('Upload initialization error:', error);
            setFormData(prev => {
                const updatedVideos = [...prev.videos];
                updatedVideos[index].isUploading = false;
                updatedVideos[index].isUploaded = false;
                return { ...prev, videos: updatedVideos };
            });
            toast.error(`Upload failed for video ${index + 1}: ${error.message}`);
        }
    };

    const removeVideo = async (index) => {
        const updatedVideos = [...formData.videos];
        const file = updatedVideos[index].video;
        updatedVideos[index].video = null;
        updatedVideos[index].isUploading = false;
        updatedVideos[index].isUploaded = false;
        setFormData(prev => ({
            ...prev,
            videos: updatedVideos
        }));

        if (file) {
            await uploadVideoToCloudflare(file, index, false, true);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setVideoData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        setVideoData(prev => ({
            ...prev,
            video: e.target.files[0]
        }));
    };

        const fetchCourseData = async () => {
            try {
                const response = await axios.get(`/api/education-course/${id}`);
                setCourse(response.data);

                // Parse videos if they exist
                if (response.data.videos) {
                    const videos = JSON.parse(response.data.videos);
                    setActiveVideo(videos[0]); // Set first video as active by default
                }
            } catch (error) {
                console.error('Error:', error);
            } finally {
                setLoading(false);
            }
        };

    const delCourseVideo = async (video_id) => {
        try {
            await axios.delete(`/api/education-course/${id}/videos/${video_id}`);
            fetchCourseData();
            Swal.fire({
                position: 'top-end',
                icon: 'success',
                title: 'Video deleted successfully',
                showConfirmButton: false,
                timer: 1500
              })
        } catch (error) {
            console.error('Error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'Something went wrong!',
              })
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();


        try {
            const response = await axios.post(`/api/education-course/${course.id}/videos`, videoData);

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: response.data?.message || 'Video added successfully',
            }).then(() => {
                fetchCourseData();
            });

            console.log(response.data);
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to add video',
            });
        } finally {
            // Close modal and reset form
            setModal(false);
            setVideoData({
                title: '',
                description: '',
                video_id: null
            });
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </motion.div>
            </div>
        );
    }

    if (!course) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                <div className="text-center">
                    <h4>Course not found</h4>
                    <Link to="/courses" className="btn btn-primary mt-3">
                        Back to Courses
                    </Link>
                </div>
            </div>
        );
    }

    const currentVideo = formData.videos[0] || {};

    const videos = course.videos ? JSON.parse(course.videos) : [];

    const totalDurationInSeconds = videos.reduce((total, video) => total + (parseFloat(video.video_duration) || 0), 0);
    const totalDuration = formatDuration(totalDurationInSeconds);
    return (
        <React.Fragment>
            <ToastContainer position="top-right" autoClose={5000} />

            {/* Add Video Modal */}
            <div className={`modal fade ${modal ? 'show d-block' : 'd-none'}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Add New Video</h5>
                            <button type="button" className="btn-close" onClick={() => setModal(false)}></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="videoTitle" className="form-label">Title</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="videoTitle"
                                        name="title"
                                        value={videoData.title}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="videoDescription" className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        id="videoDescription"
                                        name="description"
                                        rows="3"
                                        value={videoData.description}
                                        onChange={handleInputChange}
                                        required
                                    ></textarea>
                                </div>
                                <FormGroup>
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <Label>Video File *</Label>
                                        <div>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-warning"
                                                onClick={() => retryUpload(0)}
                                                disabled={currentVideo.isUploading}
                                            >
                                                Retry
                                            </button>
                                            &nbsp;&nbsp;
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => removeVideo(0)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                    <div className="video-upload-box">
                                        {currentVideo?.video ? (
                                            <div className="video-upload-preview">
                                                <div className="video-info">
                                                    <Video size={24} className="text-primary me-2" />
                                                    <div>
                                                        <div className="video-name">{currentVideo?.video?.name}</div>
                                                        <div className="video-size">
                                                            {(currentVideo?.video?.size / (1024 * 1024).toFixed(2))} MB
                                                        </div>
                                                    </div>
                                                </div>

                                                {currentVideo?.isUploading && (
                                                    <div className="upload-progress">
                                                        <Progress
                                                            value={uploadProgress[currentVideo.uploadId] || 0}
                                                            className="mb-2"
                                                        />
                                                        <div className="d-flex justify-content-between">
                                                            <small className="text-muted">
                                                                {uploadProgress[currentVideo.uploadId] || 0}% Uploaded
                                                            </small>
                                                            <Loader size={16} className="spin" />
                                                        </div>
                                                    </div>
                                                )}

                                                {currentVideo?.isUploaded && (
                                                    <div className="upload-complete text-success">
                                                        <Check size={16} className="me-1" /> Upload Complete
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <>
                                                <label className="video-upload-placeholder">
                                                    <span>Click to upload video</span>
                                                    <span className="text-muted small">(MP4, max 500MB)</span>
                                                    <Input
                                                        type="file"
                                                        name="video"
                                                        accept="video/mp4,video/x-m4v,video/*"
                                                        onChange={(e) => handleVideoChange(0, e)}
                                                        required
                                                        className="d-none"
                                                    />
                                                </label>
                                            </>
                                        )}
                                    </div>
                                </FormGroup>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary">Upload Video</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Hero Section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Row>
                    <Col lg="12">
                        <div className="course-hero position-relative rounded-lg overflow-hidden mb-4">
                            <img
                                src={`${course?.thumbnail}`}
                                alt={course?.title}
                                className="img-fluid w-100"
                                style={{ height: '400px', objectFit: 'cover' }}
                            />
                            <div className="course-hero-overlay"></div>

                            <div className="course-hero-content position-absolute p-4" style={{ bottom: 0, left: 0 }}>
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.3, duration: 0.5 }}
                                >
                                    <div className="d-flex align-items-center mb-3">
                                        <img
                                            src={getProfileImageUrl(course.creator)}
                                            alt={course?.creator?.name}
                                            className="rounded-circle mr-3"
                                            width="50"
                                            height="50"
                                        />
                                        <div>
                                            <h6 className="text-white mb-0 ms-1">Created by</h6>
                                            <h5 className="text-white mb-0 custom-font-family ms-1" style={{ textTransform: 'uppercase' }}>{course?.creator?.name}</h5>
                                        </div>
                                    </div>

                                    <h1 className="text-white mb-2 custom-font-family">{course?.title}</h1>

                                    <div className="d-flex flex-wrap custom-font-family">
                                        <Badge color="light" className="mr-2 mb-2 d-flex text-black align-items-center">
                                            <BookOpen size={14} className="me-1" /> {course?.category?.name}
                                        </Badge>
                                        &nbsp;
                                        <Badge color="light" className="mr-2 mb-2 d-flex text-black align-items-center">
                                            <Clock size={14} className="me-1" /> {totalDuration}
                                        </Badge>
                                        &nbsp;
                                        <Badge color="light" className="mr-2 mb-2 d-flex text-black     align-items-center">
                                            <BarChart2 size={14} className="me-1" /> {videos?.length} videos
                                        </Badge>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </motion.div>

            {/* Course Content */}
            <Row>
                <Col lg="8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <Card className="mb-4">
                            <CardBody>
                                <CardTitle tag="h4" className="mb-3">About This Course</CardTitle>
                                <p className="text-muted custom-font-family">{course?.description}</p>
                            </CardBody>
                        </Card>

                        {/* Video Player */}
                        <Card className="mb-4">
                            <CardBody>
                                <CardTitle tag="h4" className="mb-3">
                                    {activeVideo ? activeVideo.title : 'Select a video to play'}
                                </CardTitle>

                                {activeVideo && (
                                    <div className="video-container mb-3">
                                        <iframe
                                            src={`https://customer-a1ev8poeeldhx83e.cloudflarestream.com/${activeVideo.token}/iframe`}
                                            style={{ border: 'none', width: '100%', borderRadius: '8px' }}
                                            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                                            allowFullScreen={true}
                                            id="stream-player"
                                        ></iframe>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </motion.div>
                </Col>

                <Col lg="4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                    >
                        <Card className="mb-4">
                            <CardBody>
                                <CardTitle tag="h5" className="mb-3">Course Content</CardTitle>
                                <div
                                    className="course-video-list"
                                    style={{
                                        maxHeight: '400px', // or any height you want
                                        overflowY: 'auto',
                                        overflowX:'hidden'
                                    }}
                                >
                                    {videos.map((video, index) => (
                                        <motion.div
                                            key={index}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <div
                                                className={`d-flex align-items-center p-3 mb-2 rounded cursor-pointer ${activeVideo && activeVideo?.token === video?.token ? 'bg-light-primary' : 'bg-light'
                                                    }`}
                                                onClick={() => handleVideoPlay(video)}
                                            >
                                                {/* Thumbnail */}
                                                <div className="me-3 position-relative" style={{ width: '80px', height: '45px' }}>
                                                    <img
                                                        src={`https://customer-a1ev8poeeldhx83e.cloudflarestream.com/${video?.token}/thumbnails/thumbnail.jpg?time=1s&height=270`}
                                                        alt={video?.title}
                                                        className="img-fluid rounded"
                                                        style={{ width: '80px', height: '45px', objectFit: 'cover' }}
                                                    />
                                                    <Play
                                                        size={16}
                                                        className="text-white position-absolute"
                                                        style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                                                    />
                                                </div>

                                                {/* Video Info */}
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-0">{video?.title}</h6>
                                                    <p>{video?.description}</p>
                                                    <small className="text-muted">{formatDuration(video?.video_duration)}</small>
                                                </div>
                                                <button className="btn btn-danger" onClick={() => delCourseVideo(video?.video_id)}>Delete</button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                <button className="btn btn-primary mt-3" onClick={addvideo}>Add Video</button>
                            </CardBody>
                        </Card>

                        <Card>
                            <CardBody>
                                <CardTitle tag="h5" className="mb-3">Course Details</CardTitle>
                                {course.pdf_path_url ? <a href={course.pdf_path_url} download={`${course?.title}.pdf`} target="_blank" className="btn btn-primary mb-3">Download PDF</a> : ""}
                                <ul className="list-unstyled">
                                    <li className="mb-2">
                                        <strong>Category:</strong> <span className="custom-font-family">{course?.category?.name}</span>
                                    </li>
                                    <li className="mb-2">
                                        <strong>Created:</strong> <span className="custom-font-family">{moment(course?.created_at).format('MMMM D, YYYY')}</span>
                                    </li>
                                    <li className="mb-2">
                                        <strong>Duration:</strong> <span className="custom-font-family">{totalDuration}</span>
                                    </li>
                                    <li className="mb-2">
                                        <strong>Videos:</strong> <span className="custom-font-family">{videos.length}</span>
                                    </li>
                                    <li className="mb-2">
                                        <strong>Tags:</strong>
                                        <div className="mt-1">
                                            {course?.category?.tags?.map((tag, index) => (
                                                <Badge
                                                key={index}
                                                className="me-1 p-2"
                                                style={{
                                                  backgroundColor: 'rgba(0, 0, 0, 0.6)', // transparent black
                                                  color: '#fff', // white text for contrast
                                                  borderRadius: '4px'
                                                }}
                                              >
                                                {tag}
                                              </Badge>
                                            ))}
                                        </div>
                                    </li>
                                </ul>

                                <Button color="primary" block className="mt-3" disabled>
                                    Enroll Now
                                </Button>
                            </CardBody>
                        </Card>
                    </motion.div>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default connect(null, { setBreadcrumbItems })(CourseDetails);

// Add some custom CSS
const styles = `
.course-hero {
    position: relative;
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.course-hero-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 100%);
}

.course-hero-content {
    z-index: 1;
}

.cursor-pointer {
    cursor: pointer;
    transition: all 0.3s ease;
}

.cursor-pointer:hover {
    transform: translateY(-2px);
}

.video-container {
    position: relative;
    padding-bottom: 56.25%; /* 16:9 */
    height: 0;
    overflow: hidden;
}

.video-container iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.bg-light-primary {
    background-color: rgba(85, 110, 230, 0.1);
}
`;

// Inject styles
const styleElement = document.createElement('style');
styleElement.innerHTML = styles;
document.head.appendChild(styleElement);